import type { ApiErrorBody, ErrorCode } from '@l2u/shared/errors';
import { getAdminAccessToken } from './auth/adminToken.ts';
import { getCompanyAccessToken } from './auth/companyToken.ts';

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode | 'network';
  readonly details: unknown;

  constructor(status: number, code: ErrorCode | 'network', message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type Init = Omit<RequestInit, 'body'> & {
  body?: unknown;
  query?: Record<string, string | number | undefined | null>;
};

async function request(path: string, init: Init = {}, headers: Record<string, string> = {}): Promise<unknown> {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  if (init.query) {
    for (const [k, v] of Object.entries(init.query)) {
      if (v === undefined || v === null) continue;
      url.searchParams.set(k, String(v));
    }
  }

  const finalHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...headers,
    ...(init.headers as Record<string, string> | undefined),
  };

  const fetchInit: RequestInit = { headers: finalHeaders };
  if (init.method !== undefined) fetchInit.method = init.method;
  if (init.credentials !== undefined) fetchInit.credentials = init.credentials;
  if (init.body !== undefined && init.body !== null) {
    fetchInit.body = JSON.stringify(init.body);
    finalHeaders['Content-Type'] = 'application/json';
  }

  let res: Response;
  try {
    res = await fetch(url.toString(), fetchInit);
  } catch (err) {
    throw new ApiError(0, 'network', err instanceof Error ? err.message : 'Network error');
  }

  if (res.status === 204) return null;

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const body = parsed as ApiErrorBody | null;
    const code = (body?.error?.code as ErrorCode | undefined) ?? 'internal';
    const message = body?.error?.message ?? `Request failed (${res.status})`;
    throw new ApiError(res.status, code, message, body?.error?.details);
  }

  return parsed;
}

function adminBearerHeaders(): Record<string, string> {
  const token = getAdminAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const adminApi = {
  get<T>(path: string, init?: Init): Promise<T> {
    return request(path, { ...init, method: 'GET' }, adminBearerHeaders()) as Promise<T>;
  },
  post<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return request(path, { ...init, method: 'POST', body }, adminBearerHeaders()) as Promise<T>;
  },
  patch<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return request(path, { ...init, method: 'PATCH', body }, adminBearerHeaders()) as Promise<T>;
  },
  put<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return request(path, { ...init, method: 'PUT', body }, adminBearerHeaders()) as Promise<T>;
  },
  delete<T>(path: string, init?: Init): Promise<T> {
    return request(path, { ...init, method: 'DELETE' }, adminBearerHeaders()) as Promise<T>;
  },
};

function bearerHeaders(): Record<string, string> {
  const token = getCompanyAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const companyApi = {
  get<T>(path: string, init?: Init): Promise<T> {
    return request(path, { ...init, method: 'GET' }, bearerHeaders()) as Promise<T>;
  },
  post<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return request(path, { ...init, method: 'POST', body }, bearerHeaders()) as Promise<T>;
  },
  patch<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return request(path, { ...init, method: 'PATCH', body }, bearerHeaders()) as Promise<T>;
  },
  put<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return request(path, { ...init, method: 'PUT', body }, bearerHeaders()) as Promise<T>;
  },
  delete<T>(path: string, init?: Init): Promise<T> {
    return request(path, { ...init, method: 'DELETE' }, bearerHeaders()) as Promise<T>;
  },
};

export const publicApi = {
  get<T>(path: string, init?: Init): Promise<T> {
    return request(path, { ...init, method: 'GET' }) as Promise<T>;
  },
  post<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return request(path, { ...init, method: 'POST', body }) as Promise<T>;
  },
};

export async function uploadFileToSignedUrl(signedUrl: string, file: File): Promise<void> {
  const res = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!res.ok) {
    throw new ApiError(res.status, 'internal', `Upload failed (${res.status})`);
  }
}
